// src/Contexts/IconContext.js

import React, { createContext, useContext, useState, useEffect } from 'react';
import { collection, doc, onSnapshot } from 'firebase/firestore';
import { db } from '../Firebase/firebase'; // Removed auth dependency here

const IconContext = createContext();

export function useIcons() {
  return useContext(IconContext);
}

export function IconProvider({ children }) {
  // --- STATE ---
  const [markerIcons, setMarkerIcons] = useState([]); // List of all icons
  const [iconsById, setIconsById] = useState({}); // Lookup Map: { id: {data} }

  // Defaults
  const [defaultIcon, setDefaultIcon] = useState(null);
  const [defaultIconID, setDefaultIconID] = useState(null);
  const [iconScale, setIconScale] = useState(100); // Default scale percentage

  const [loading, setLoading] = useState(true);

  // --- FETCH MARKER ICONS (Unconditional) ---
  // AMENDED: Removed the onAuthStateChanged wrapper so public clients download marker icons
  useEffect(() => {
    // Listen to Settings for Icon Scale
    const unsubscribeSettings = onSnapshot(
      doc(db, 'settings', 'IconScale'),
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          const scaleVal = typeof data.scale === 'number' ? data.scale : 100;
          setIconScale(scaleVal);
        } else {
          setIconScale(100);
        }
      },
      (error) => {
        console.error('[IconContext] Error fetching IconScale settings:', error);
      },
    );

    // Listen to Marker Icons
    const unsubscribeFirestore = onSnapshot(
      collection(db, 'markerIcons'),
      (snapshot) => {
        const fetchedData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Sort: Default first, then Alphabetical
        fetchedData.sort((a, b) => {
          if (a.isDefault && !b.isDefault) return -1;
          if (!a.isDefault && b.isDefault) return 1;
          return (a.name || '').localeCompare(b.name || '');
        });

        setMarkerIcons(fetchedData);

        // --- BUILD LOOKUP MAP ---
        const lookup = {};
        fetchedData.forEach((icon) => {
          lookup[icon.id] = icon;
        });
        setIconsById(lookup);

        // --- IDENTIFY DEFAULTS ---
        const defaultItem = fetchedData.find((c) => c.isDefault);
        const defIcon = defaultItem ? defaultItem.icon : fetchedData[0]?.icon || null;
        const defId = defaultItem ? defaultItem.id : fetchedData[0]?.id || null;

        setDefaultIcon(defIcon);
        setDefaultIconID(defId);

        // --- PRELOAD IMAGES ---
        fetchedData.forEach((item) => {
          if (item.icon) {
            const img = new Image();
            img.src = item.icon;
          }
        });

        setLoading(false);
      },
      (error) => {
        console.error('[IconContext] Error fetching markerIcons:', error);
        setLoading(false);
      },
    );

    // Cleanup on unmount
    return () => {
      if (unsubscribeFirestore) unsubscribeFirestore();
      if (unsubscribeSettings) unsubscribeSettings();
    };
  }, []);

  const value = {
    markerIcons, // List for the Tray
    iconsById, // Lookup for MarkerManager
    defaultIcon, // Fallback URL
    defaultIconID, // Fallback ID
    iconScale, // The global scale percentage (e.g., 100, 150)
    loading,
  };

  return <IconContext.Provider value={value}>{children}</IconContext.Provider>;
}
